import { AST, ASTAdditionalDataFromServer } from 'mk2/apps/wiki/schemas';
import { Entity, PhotoEntity, PhotoSchema as PhotoFileSchema } from 'mk2/schemas';
import { schema } from 'normalizr';

export interface TwoDimsVariant {
    width: number;
    depth: number;
    variant: string;
}

export interface StrollerUserReviewPhotoEntity extends Entity {
    review?: StrollerUserReviewEntity;
    photoFile: PhotoEntity;
    position: number;
}

export interface StrollerUserReviewEntity extends Entity {
    score: number;
    stroller?: StrollerEntity;
    username: string;
    isRegisteredUser: boolean;
    oneliner: string;
    perex: string;
    detailedReview: boolean;
    showDetailLink: boolean;
    createdTime: string;
    text: string;
    positives: string[];
    negatives: string[];
    photos: StrollerUserReviewPhotoEntity[];
}

export interface SiblingSlugs {
    201?: string;
    202?: string;
    207?: string;
}

export interface StrollerStrollerPhoto extends Entity {
    photo: StrollerPhoto;
    stroller?: StrollerEntity;
    orderIndex: number;
}

export interface Features {
    a_bag_can_be_clicked_on_instead_of_a_seat_unit?: boolean;
    a_crotch_safety_strap_is_present?: boolean;
    a_second_basket_can_be_attached_on_the_stroller?: boolean;
    a_tablet_pocket_in_the_hood?: boolean;
    adjustable_handle?: boolean;
    adjustable_suspension?: boolean;
    all_wheels_are_double?: boolean;
    amazon_widget?: string;
    artificial_wheel_type?: string[];
    autolock?: boolean;
    back_support_can_be_extended?: boolean;
    back_support_can_be_in_lying_position?: boolean;
    back_support_can_be_positioned?: boolean;
    back_support_length_in_cm_max?: number;
    back_support_length_in_cm_min?: number;
    back_support_positioning_type?: string[];
    back_support_positions_l?: number[];
    big_shopping_basket?: boolean;
    bucket_seat?: boolean;
    bucket_second_seat?: boolean;
    builtin_speaker?: boolean;
    bumper_bar?: boolean;
    bumper_bar_can_be_bought_separately?: boolean;
    bumper_bar_can_be_positioned?: boolean;
    bumper_bar_can_be_removed?: boolean;
    bumper_bar_equipped_with_a_removable_cover?: boolean;
    bumper_bar_type?: string[];
    can_be_converted_to_a_double?: boolean;
    can_be_converted_to_a_single?: boolean;
    can_be_pulled_behind?: boolean;
    car_seat_can_be_attached?: string[];
    car_seat_compatibility_most_brands?: boolean;
    car_seat_compatibility_roemer?: boolean;
    car_seat_has_to_be_bought?: string;
    carrycot_can_be_attached?: string[];
    carrycot_dims_in_cm?: TwoDimsVariant[];
    carrycot_has_handle?: string[];
    carrycot_has_ventilation?: string[];
    carrycot_usable_in_car?: string[];
    carrycot_with_cradle?: string[];
    central_brake_present?: boolean;
    central_brake_system?: string[];
    checked_baggage?: boolean;
    cleaning_the_stroller_fabrics?: string;
    closable_peek_a_boo_window?: boolean;
    combo_only?: boolean;
    cupholder_possibility?: boolean;
    discontinued?: boolean;
    discontinued_year?: number;
    extendable_hood?: boolean;
    foldable_carrycot?: string[];
    foldable_handrail?: boolean;
    folded_dimensions_in_cm_height_max?: number;
    folded_dimensions_in_cm_height_min?: number;
    folded_dimensions_in_cm_length_max?: number;
    folded_dimensions_in_cm_length_min?: number;
    folded_dimensions_in_cm_width_max?: number;
    folded_dimensions_in_cm_width_min?: number;
    folding_system?: string[];
    folds_by_itself?: boolean;
    folds_with_both_seats_attached?: boolean;
    folds_with_seat_outwards?: boolean;
    folds_with_the_seat_attached?: boolean;
    folds_with_the_seat_in_reverse?: boolean;
    follow_the_sun_hood?: boolean;
    foot_rest?: boolean;
    front_wheel_type?: string[];
    front_wheels_diameter_in_cm_l?: number[];
    fully_fledged_second_seat?: boolean;
    handle_height_in_cm_max?: number;
    handle_height_in_cm_min?: number;
    handle_material?: string[];
    harness_padding?: string;
    harness_type?: string;
    has_carry_handle?: boolean;
    height_adjustable_harness?: boolean;
    height_adjustable_hood?: boolean;
    height_adjustable_seat?: boolean;
    higher_positioned_carrycot?: boolean;
    higher_seat_position?: boolean;
    hood_equipped_with_a_pocket?: boolean;
    hood_equipped_with_uv_protection?: boolean;
    hood_equipped_with_ventilation_panel?: boolean;
    hood_features_a_sun_visor?: boolean;
    hood_with_clasps_on_the_sides?: boolean;
    inclinable_carrycot?: string[];
    led_lighting?: boolean;
    leg_rest_can_be_extended?: boolean;
    leg_rest_can_be_positioned?: boolean;
    leg_rest_positions?: number;
    manual_brake_present?: boolean;
    maximum_load_per_1st_child_in_kg?: number;
    maximum_load_per_2nd_child_in_kg?: number;
    maximum_load_per_child_in_kg?: number;
    memory_button?: boolean;
    missing_sitting_part?: boolean;
    model_url?: string;
    motorized?: boolean;
    number_of_double_wheels?: number;
    number_of_wheels_l?: number[];
    one_hand_folding?: boolean;
    one_hand_positioning?: boolean;
    origin_countries?: string[];
    parent_tray_organizer?: boolean;
    peek_a_boo_window_in_the_hood?: boolean;
    peek_a_boo_window_with_silent_closure?: boolean;
    platform_material?: string[];
    possibility_of_terrain_wheels?: boolean;
    pramette_seat_2in1?: boolean;
    rear_wheel_type?: string[];
    rear_wheels_diameter_in_cm_l?: number[];
    recommended_age_max?: number;
    recommended_age_min?: number;
    removable_hood?: boolean;
    retroreflector?: boolean;
    roof_present?: boolean;
    rotating_seat?: boolean;
    rotating_second_seat?: boolean;
    rotating_wheels?: boolean;
    seat_dims_in_cm_depth?: number;
    seat_dims_in_cm_width?: number;
    seat_fabrics_can_be_taken_off?: boolean;
    seat_safety_indicator?: boolean;
    seat_surface_total_length_in_cm_max?: number;
    seat_surface_total_length_in_cm_min?: number;
    seat_unit_stroller_body_equipped_with_ventilation_system?: boolean;
    second_seat_back_support_can_be_in_lying_position?: boolean;
    second_seat_back_support_can_be_positioned?: boolean;
    second_seat_back_support_length_in_cm_max?: number;
    second_seat_back_support_length_in_cm_min?: number;
    second_seat_back_support_positioning_type?: string[];
    second_seat_back_support_positions_l?: number[];
    second_seat_bumper_bar?: boolean;
    second_seat_dims_in_cm_depth?: number;
    second_seat_dims_in_cm_width?: number;
    second_seat_foot_rest?: boolean;
    second_seat_leg_rest?: boolean;
    second_seat_one_hand_positioning?: boolean;
    second_seat_recommended_age_max?: number;
    second_seat_recommended_age_min?: number;
    second_seat_roof_present?: boolean;
    second_seat_surface_total_length_in_cm_max?: number;
    second_seat_surface_total_length_in_cm_min?: number;
    shopping_basket?: boolean;
    shopping_basket_capacity_in_kg?: number;
    shopping_basket_closable?: boolean;
    sportgolf_only?: boolean;
    standing_fold?: boolean;
    stroller_body_equipped_with_a_pocket?: boolean;
    supplied_cape?: boolean;
    supplied_changing_bag?: string;
    supplied_footmuff?: boolean;
    supplied_mosquito_net?: boolean;
    supplied_pad?: boolean;
    supplied_transport_bag?: boolean;
    supplied_wheel_protective_sleeves?: boolean;
    suspended_wheels?: string;
    suspension?: boolean;
    suspension_type?: string[];
    swing_away_bumper_bar?: boolean;
    swinging?: boolean;
    two_car_seats_can_be_attached?: boolean;
    two_wheel_mode?: boolean;
    type?: string[];
    type_of_handle_adjustment?: string[];
    unfolded_dimensions_in_cm_height_max?: number;
    unfolded_dimensions_in_cm_height_min?: number;
    unfolded_dimensions_in_cm_length_max?: number;
    unfolded_dimensions_in_cm_length_min?: number;
    unfolded_dimensions_in_cm_width_max?: number;
    unfolded_dimensions_in_cm_width_min?: number;
    video_url?: object[];
    waterproof_hood?: boolean;
    weight_in_kg_max?: number;
    weight_in_kg_min?: number;
    wheel_direction_memory?: boolean;
    wheel_mudguards?: boolean;
    wheels_can_be_arrested?: boolean;
    wheels_can_be_removed?: boolean;
    wipe_clean_leg_rest?: boolean;
    wrist_safety_strap?: boolean;
}

export interface StrollerFeaturesEntity extends Entity, Features {}

export interface StrollerQuestionEntity extends Entity {
    stroller: StrollerEntity;
    question: string;
    answer: string;
}

export interface CountryEntity extends Entity {
    msgid: string;
}

export interface StrollerCountryEntity extends Entity {
    country: CountryEntity;
    stroller?: StrollerEntity;
}

interface StrollerTextEntity extends Entity {
    description: string;
}

export interface StrollerEntity extends Entity {
    name: string;
    slug: string;
    siblingSlugs: SiblingSlugs;
    prepared: boolean;
    ourScore: number;
    userScore: number;
    description: StrollerTextEntity;
    features: StrollerFeaturesEntity;
    countries: StrollerCountryEntity[];
    photos: StrollerStrollerPhoto[];
    reviews: StrollerUserReviewEntity[];
    questions: StrollerQuestionEntity[];
    brand: StrollerBrandEntity;
}

export interface StrollerPhoto {
    id: number;
    originalWidth: number;
    originalHeight: number;
    url100x100: string;
    url250x250: string;
    url720x720: string;
}

export interface StrollerBrandEntity extends Entity {
    name: string;
    slug: string;
    descriptionAST: AST;
    descriptionASTAdditionalData: ASTAdditionalDataFromServer;
    brandUrl: string;
    logoPhoto: StrollerPhoto;
    strollerPhoto: StrollerPhoto;
}

export const StrollerSchema = new schema.Entity('strollers.stroller');
export const CountrySchema = new schema.Entity('strollers.country');
export const PhotoSchema = new schema.Entity('strollers.photo');
export const StrollerBrandSchema = new schema.Entity('strollers.strollerbrand', {
    logoPhoto: PhotoSchema,
    strollerPhoto: PhotoSchema,
});
export const StrollerPhotoSchema = new schema.Entity('strollers.strollerphoto', {
    stroller: StrollerSchema,
    photo: PhotoSchema,
});
export const StrollerTextSchema = new schema.Entity('strollers.strollertext');
export const StrollerFeaturesSchema = new schema.Entity('strollers.strollerfeatures', {
    stroller: StrollerSchema,
});
export const StrollerCountrySchema = new schema.Entity('strollers.strollercountry', {
    stroller: StrollerSchema,
    country: CountrySchema,
});
export const StrollerUserReviewSchema = new schema.Entity('strollers.strolleruserreview');
export const StrollerUserReviewPhotoSchema = new schema.Entity('strollers.strolleruserreviewphoto', {
    review: StrollerUserReviewSchema,
    photoFile: PhotoFileSchema,
});
export const StrollerQuestionSchema = new schema.Entity('strollers.strollerquestion', {
    stroller: StrollerSchema,
});

// Recursive schema
StrollerUserReviewSchema.define({
    stroller: StrollerSchema,
    photos: [StrollerUserReviewPhotoSchema],
});
StrollerSchema.define({
    photos: [StrollerPhotoSchema],
    brand: StrollerBrandSchema,
    description: StrollerTextSchema,
    features: StrollerFeaturesSchema,
    countries: [StrollerCountrySchema],
    reviews: [StrollerUserReviewSchema],
    questions: [StrollerQuestionSchema],
});
